import React, {
    createContext,
    useContext,
    useEffect,
    useState
} from 'react';

import { hot } from 'react-hot-loader';
import { useLazyQuery, useMutation } from '@apollo/client';
import { connect } from 'react-redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import withPageFlow from '../../components/PageFlow/PageFlow';
import { BED_BATH_PAGE, VRBO_HANDLER } from '../../components/PageFlow/PageList';
import { CREATE_PROPERTY, FETCH_VRBO_USER_INFO } from './graphql/query';
import { buildPayload, onComplete, onError } from './utils/propertyCreation';
import { mapDispatchToProps, mapStateToProps, proptypes } from './properties';
import { ExperimentCtx } from '../experiments';
import { EGTNL_TESTS_MPAAS } from '../../../server/api/abacus/constants';
import log from '../../utils/ClientLogger';

export const VrboCtx = createContext();

export function VrboHandler(props) {
    const {
        children,
        setCreatePropertyError,
        eloquaValue,
        setEloquaValue,
        propertyCreationProcess,
        startPropertyCreationProcess,
        finishPropertyCreationProcess
    } = props;
    const { experimentEvaluations } = useContext(ExperimentCtx);
    const { trigger } = useContext(TrackingContext);
    const [loading, setLoading] = useState(false);
    const [createProperty, createPropertyState] = useMutation(CREATE_PROPERTY);
    const [fetchVrboUserInfo] = useLazyQuery(FETCH_VRBO_USER_INFO);

    const {
        [EGTNL_TESTS_MPAAS.ULX_ADDRESSV2_VR]: isAddressV2,
        [EGTNL_TESTS_MPAAS.ULX_PRODUCT_LISTING_MIGRATION]: isProductListingMigrationOn
    } = experimentEvaluations || {};

    useEffect(() => {
        setLoading(createPropertyState.loading || propertyCreationProcess?.start);
    }, [createPropertyState.loading, propertyCreationProcess?.start]);

    useEffect(() => {
        if (propertyCreationProcess?.start) {
            const callCreateProperty = async (pageName = 'Estimateresults') => {
                setCreatePropertyError(null);
                const trustPayload = window?.trustApi?.getTrustPayload();
                const vrboUserInfoResponse = await fetchVrboUserInfo({
                    variables: {
                        // eslint-disable-next-line react/destructuring-assignment
                        locale: props.locale
                    },
                    fetchPolicy: 'no-cache'
                });

                const {
                    phoneNumber, phoneCountryCode, phoneCountryCodeDigits
                } = vrboUserInfoResponse?.data?.fetchVrboUserInformation || {};

                const propsToPass = {
                    ...props,
                    phoneNumber,
                    phoneCountryCode,
                    phoneCountryCodeDigits,
                    trigger,
                    pageName,
                    competitorLinkValue: eloquaValue,
                    trustPayload,
                    isAddressV2: isAddressV2 === 1,
                    productListingMigration: isProductListingMigrationOn === 1
                };
                // lets move this logic to a helper function
                const {
                    pageFlow
                } = propsToPass;
                const hasValidPhone = phoneNumber && phoneNumber.trim() !== ''
            && phoneCountryCode && phoneCountryCodeDigits;

                let phoneSource = 'none';
                if (phoneNumber && phoneNumber.trim() !== '') {
                    phoneSource = pageName === 'MissingDetails' ? 'user_input' : 'authInfo';
                }

                if (!hasValidPhone) {
                    trigger('outcome', {
                        category: 'property_creation',
                        action: 'blocked_missing_phone',
                        label: pageName
                    });
                    finishPropertyCreationProcess();

                    if (pageFlow?.navigate) {
                        try {
                            await pageFlow.navigate('MissingDetails');
                            log(['info'], 'property_creation_flow', {
                                event: 'phone_redirect',
                                pageName,
                                phoneSource,
                                hasCountryCode: !!phoneCountryCode,
                                hasCountryCodeDigits: !!phoneCountryCodeDigits,
                                outcome: 'redirected_to_MissingDetails'
                            });
                        } catch (error) {
                            log(['error'], 'property_creation_flow', {
                                event: 'phone_redirect_error',
                                pageName,
                                errorType: error.name,
                                errorMessage: error.message
                            });
                        }
                    } else {
                        log(['error'], 'property_creation_flow', {
                            event: 'phone_redirect_error',
                            pageName,
                            reason: 'navigate_unavailable'
                        });
                    }
                    return;
                }

                log(['info'], 'property_creation_flow', {
                    event: 'create_property',
                    pageName,
                    phoneSource
                });

                const payload = buildPayload(propsToPass);
                await createProperty({
                    variables: {
                        property: payload
                    }
                }).then(
                    (data) => { onComplete({ ...propsToPass })(data); },
                    onError(propsToPass)
                )
                    .catch(onError(propsToPass))
                    .finally(finishPropertyCreationProcess);
            };
            callCreateProperty(propertyCreationProcess.pageName);
        }
    }, [propertyCreationProcess?.start]);

    const triggerPropertyCreation = (pageName = 'Estimateresults') => {
        startPropertyCreationProcess(pageName);
    };

    const restartPropertyCreationProcess = () => {
        const { pageFlow } = props;
        setCreatePropertyError(null);
        if (pageFlow?.navigate) {
            pageFlow.navigate(BED_BATH_PAGE);
        }
    };

    return (
        <VrboCtx.Provider value={{
            loading,
            setCompetitorLinkValue: setEloquaValue,
            triggerPropertyCreation,
            restartPropertyCreationProcess
        }}
        >
            {children}
        </VrboCtx.Provider>
    );
}

VrboHandler.propTypes = proptypes;
const connectedVrboHandler = connect(mapStateToProps, mapDispatchToProps)(VrboHandler);

connectedVrboHandler.displayName = VRBO_HANDLER;
export default hot(module)(withPageFlow(connectedVrboHandler));
